/**
 * @fileoverview Create/edit dialog for a tag (name only).
 * @module features/tags/components/TagFormDialog
 */
import { useEffect, useMemo, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type TagFormValues = {
  name: string
}

interface TagFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initial?: { name: string } | undefined
  serverError?: string | null
  onSubmit: (values: { name: string }) => Promise<void>
  onOpenChange: (open: boolean) => void
}

export function TagFormDialog({
  open,
  mode,
  initial,
  serverError,
  onSubmit,
  onOpenChange,
}: TagFormDialogProps): ReactElement {
  const { t } = useTranslation('features-tags')
  const tagFormSchema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, t('form.nameRequired', 'Name is required')),
      }),
    [t]
  )
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<TagFormValues>({
    resolver: zodResolver(tagFormSchema),
    defaultValues: { name: initial?.name ?? '' },
  })

  useEffect(() => {
    if (open) {
      reset({ name: initial?.name ?? '' })
    }
  }, [open, initial, reset])

  const submit = handleSubmit(values => onSubmit({ name: values.name.trim() }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? t('form.createTitle', 'New tag')
              : t('form.editTitle', 'Edit tag')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'form.description',
              'Tags group products so collection-URL ad spend can be attributed to them.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form id="tag-form" onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tag-name">{t('form.nameLabel', 'Name')}</Label>
            <Input id="tag-name" autoFocus {...register('name')} />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          {serverError && <p className="text-xs text-destructive">{serverError}</p>}
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t('form.cancel', 'Cancel')}
          </Button>
          <Button type="submit" form="tag-form" disabled={isSubmitting}>
            {mode === 'create' ? t('form.create', 'Create') : t('form.save', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
